import { type ComponentProps, type KeyboardEvent, useId, useRef, useState } from 'react';
import { Calendar as CalendarIcon } from 'lucide-react';

import { Button } from '../Button/Button';
import { Calendar } from '../Calendar/Calendar';
import { InputGroup, InputGroupIcon, InputGroupInput } from '../Input/InputGroup';
import { Popover, PopoverAnchor, PopoverContent } from '../Popover/Popover';
import { isoDay, parseClock, parseIsoDay, parseTypedDay } from './dates';
import { formatDashedDate } from '@/lib/format';
import { cn } from '@/util/utils';

/*
  The design's date field, the prototype's ui-date (form.js, form.css): a bordered text box with a
  calendar icon, and a one-month calendar under it (ALS-227).

  It replaces `<input type="date">` in every form. That renders the browser's own picker in the
  browser's own format, Chrome's "dd.mm.yyyy" and its blue calendar here, which is what Franka
  raised on ALS-227 and on 29 Aug before it.

  THE BOX STAYS TYPABLE, as ui-date's is: "the picker is an offer, not a gate". A date is written
  the prototype's way, 16-Jun-2026, and typing one in that shape (or as 2026-06-16) sets it, which
  is the quickest way to a date years away and the way a keyboard user never has to leave the box.
  ArrowDown moves into the calendar, whose days are real buttons; Escape closes it.

  The value going in and out is what the native control spoke, "2026-06-16", or with `time`
  "2026-06-16T14:30", so a form swaps one for the other and its state and its API calls do not
  change. The calendar renders in a portal, so a dialog cannot clip it, which is the bug the
  prototype's own popover had until 24 Sep.
*/

export interface DateFieldProps extends Omit<
    ComponentProps<'input'>,
    'value' | 'onChange' | 'type' | 'min' | 'max'
> {
    /** "2026-06-16", or "2026-06-16T14:30" with `time`; empty when nothing is chosen. */
    value: string;
    onChange: (value: string) => void;
    /** Adds the prototype's clock row under the month; the value then carries a time. */
    time?: boolean;
    /** The earliest day that can be chosen, "2026-06-16". Earlier days are drawn but disabled. */
    min?: string;
}

/** What a picked day with no time yet gets, so a date-and-time value is never half a value. */
const DEFAULT_CLOCK = '09:00';

export function DateField({
    value,
    onChange,
    time = false,
    min,
    placeholder = 'Select a date',
    disabled,
    className,
    onBlur,
    ...props
}: DateFieldProps) {
    const [open, setOpen] = useState(false);
    // What is being typed, until it is left; null shows the value as the prototype writes it.
    const [draft, setDraft] = useState<string | null>(null);
    const day = parseIsoDay(value);
    const clock = time ? value.slice(11, 16) : '';
    const earliest = min ? parseIsoDay(min) : null;
    const [month, setMonth] = useState<Date>(day ?? new Date());
    const input = useRef<HTMLInputElement>(null);
    const anchor = useRef<HTMLDivElement>(null);
    const calendar = useRef<HTMLDivElement>(null);
    // Focus handed back to the box after a pick must not open the calendar straight back up.
    const refocusing = useRef(false);
    const clockId = useId();

    const shown =
        draft ?? (day ? formatDashedDate(day) + (time && clock ? ` · ${clock}` : '') : '');

    function emit(next: Date | null, nextClock = clock) {
        if (next === null) return onChange('');
        const iso = isoDay(next);
        onChange(time ? `${iso}T${nextClock || DEFAULT_CLOCK}` : iso);
    }

    function show() {
        if (disabled) return;
        setMonth(day ?? earliest ?? new Date());
        setOpen(true);
    }

    function type(text: string) {
        setDraft(text);
        if (text.trim() === '') return emit(null);
        // "16-Jun-2026 · 14:30" as the field writes it, or a pasted "2026-06-16T14:30".
        const iso = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/.exec(text.trim());
        const [datePart, clockPart] = iso
            ? [iso[1], iso[2]]
            : text.split('·').map((part) => part.trim());
        const typed = parseTypedDay(datePart);
        if (typed === null || (earliest && typed < earliest)) return;
        const typedClock = clockPart && parseClock(clockPart);
        emit(typed, typedClock || clock);
        setMonth(typed);
    }

    function pick(picked: Date | undefined) {
        // A second click on the chosen day would clear it; a date field keeps its day instead.
        if (!picked) return;
        setDraft(null);
        emit(picked);
        // With a clock beneath, the calendar stays open for the time, as ui-date's does.
        if (time) return;
        setOpen(false);
        refocusing.current = true;
        input.current?.focus();
    }

    function keyDown(event: KeyboardEvent<HTMLInputElement>) {
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            show();
            // After the calendar has painted: the chosen day, else today, else the first day.
            requestAnimationFrame(() =>
                calendar.current
                    ?.querySelector<HTMLButtonElement>(
                        '[data-selected-single=true], .rdp-today button, .rdp-day:not(.rdp-outside) button:not(:disabled)',
                    )
                    ?.focus(),
            );
        } else if (event.key === 'Enter' && open) {
            // Closes the calendar rather than submitting the form the field sits in.
            event.preventDefault();
            setOpen(false);
        }
    }

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverAnchor asChild>
                <InputGroup ref={anchor} data-slot="date-field" className={className}>
                    <InputGroupIcon>
                        {/* A toggle for the pointer; the keyboard has ArrowDown and focus. */}
                        <button
                            type="button"
                            tabIndex={-1}
                            aria-hidden
                            disabled={disabled}
                            className="flex cursor-pointer disabled:cursor-not-allowed"
                            onMouseDown={(event) => {
                                event.preventDefault();
                                if (open) setOpen(false);
                                else {
                                    show();
                                    refocusing.current = true;
                                    input.current?.focus();
                                }
                            }}
                        >
                            <CalendarIcon />
                        </button>
                    </InputGroupIcon>
                    <InputGroupInput
                        ref={input}
                        {...props}
                        type="text"
                        inputMode="text"
                        autoComplete="off"
                        disabled={disabled}
                        placeholder={placeholder}
                        value={shown}
                        aria-haspopup="dialog"
                        aria-expanded={open}
                        onChange={(event) => type(event.target.value)}
                        onKeyDown={keyDown}
                        onFocus={() => {
                            if (refocusing.current) refocusing.current = false;
                            else show();
                        }}
                        onBlur={(event) => {
                            setDraft(null);
                            onBlur?.(event);
                        }}
                    />
                </InputGroup>
            </PopoverAnchor>
            <PopoverContent
                ref={calendar}
                align="start"
                className="flex w-auto flex-col gap-3"
                // Focus stays in the box: typing is the other way in, and it must keep working.
                onOpenAutoFocus={(event) => event.preventDefault()}
                onCloseAutoFocus={(event) => event.preventDefault()}
                // A press on the box itself is not "outside"; it is the reader still in the field.
                onInteractOutside={(event) => {
                    if (anchor.current?.contains(event.target as Node)) event.preventDefault();
                }}
            >
                <Calendar
                    mode="single"
                    selected={day ?? undefined}
                    onSelect={pick}
                    month={month}
                    onMonthChange={setMonth}
                    disabled={earliest ? { before: earliest } : undefined}
                />
                {time ? (
                    <ClockRow
                        id={clockId}
                        clock={clock}
                        onClock={(next) => next && day && emit(day, next)}
                        onDone={() => setOpen(false)}
                    />
                ) : null}
            </PopoverContent>
        </Popover>
    );
}

/**
 * ui-date's clock row (2934:5932): "Time", a 24-hour hh:mm box and Done. A text box rather than
 * `<input type="time">`, for the reason the day is not a native control either. Shared with
 * DateTimeField, so every date-and-time picker has the one row. `onClock` hears `null` while
 * the box does not hold a time.
 */
export function ClockRow({
    id,
    clock,
    onClock,
    onDone,
    doneDisabled = false,
}: {
    id: string;
    clock: string;
    onClock: (clock: string | null) => void;
    onDone: () => void;
    doneDisabled?: boolean;
}) {
    const [text, setText] = useState(clock);
    return (
        <div className="border-border flex items-center gap-2 border-t pt-3">
            <label htmlFor={id} className="text-label-sm text-muted-foreground">
                Time
            </label>
            <input
                id={id}
                type="text"
                inputMode="numeric"
                placeholder="hh:mm"
                maxLength={5}
                value={text}
                onChange={(event) => {
                    setText(event.target.value);
                    onClock(parseClock(event.target.value));
                }}
                className={cn(
                    // ui-date__clock-field: 88 wide on the page ground, the time centred. Typed
                    // into, so 16px on touch, where iOS zooms into anything smaller.
                    'border-input bg-background focus-visible:border-ring focus-visible:ring-outline h-8 w-[88px] rounded-md border px-2 text-center text-sm outline-none focus-visible:ring-[3px] pointer-coarse:h-11 pointer-coarse:text-base',
                )}
            />
            <Button
                type="button"
                size="sm"
                className="ml-auto"
                disabled={doneDisabled}
                onClick={onDone}
            >
                Done
            </Button>
        </div>
    );
}
